/**
 * CartItemRow.jsx — Primitive UI : une ligne d'article dans le CartDrawer.
 *
 * §11/§18 — Composant primitif ; importé par CartDrawer.
 * §20 — Tous les tokens depuis theme.js (zéro littéral couleur/font/radius local).
 * §12 ter — Zéro px en dur pour espacements/polices.
 * §12 undecies — Casse grammaticale (aucun text-transform:uppercase).
 * §12 terdecies — Aucun composant natif navigateur pour interaction.
 */
import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { removeFromCart } from '../../../store/slices/cartSlice';
import SmartImage from '../SmartImage';
import {COLORS, FONTS, RADII, media, FONT_SIZE, FONT_WEIGHT} from '../theme';

/* ─── Helpers ──────────────────────────────────────────────────────────────── */

const ITEM_TYPE_LABELS = {
  land: 'Land',
  land_group: 'Land Group',
  collectible: 'Collectible',
  nft: 'Collectible',           // legacy alias → affiche "Collectible"
  tile: 'Land',                 // alias backend
  tilegroup: 'Land Group',      // alias backend
  earnicoins_pack: 'Earnicoins', // pack earnicoins one-time purchase
};

function getItemTypeLabel(itemType) {
  if (!itemType) return '';
  return ITEM_TYPE_LABELS[itemType.toLowerCase()] || itemType;
}

/* ─── Styled components ─────────────────────────────────────────────────────── */

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 0.0625rem solid ${COLORS.inkLine};
  background: ${({ $unavailable }) => $unavailable ? COLORS.subtle : COLORS.white};
  opacity: ${({ $unavailable }) => $unavailable ? 0.65 : 1};
  transition: background 0.12s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ $unavailable }) => $unavailable ? COLORS.subtle : COLORS.bg};
  }

  ${media.sm`
    padding: 0.625rem 0.75rem;
    gap: 0.625rem;
  `}
`;

const UnavailableBadge = styled.span`
  display: inline-block;
  font-family: ${FONTS.head};
  font-size: 0.7rem;
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.danger};
  background: ${COLORS.dangerSoft};
  border-radius: ${RADII.pill};
  padding: 0.1rem 0.45rem;
  align-self: flex-start;
`;

const Thumb = styled(SmartImage)`
  width: 3rem;
  height: 3rem;
  border-radius: ${RADII.button};
  object-fit: cover;
  flex: 0 0 3rem;
  background: ${COLORS.subtle};
`;

const Info = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const ItemName = styled.span`
  font-family: ${FONTS.head};
  font-size: ${FONT_SIZE.sm};
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMeta = styled.span`
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TypeChip = styled.span`
  display: inline-block;
  font-family: ${FONTS.head};
  font-size: 0.7rem;
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.accent};
  background: ${COLORS.accentSoft};
  border-radius: ${RADII.pill};
  padding: 0.1rem 0.45rem;
  align-self: flex-start;
`;

const PriceText = styled.span`
  font-family: ${FONTS.mono};
  font-size: ${FONT_SIZE.sm};
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.ink};
  flex: 0 0 auto;
  white-space: nowrap;
`;

const RemoveBtn = styled.button`
  flex: 0 0 auto;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: ${RADII.circle};
  border: 0.0625rem solid ${COLORS.inkLine};
  background: transparent;
  color: ${COLORS.muted};
  font-size: ${FONT_SIZE.base};
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;

  &:hover {
    background: ${COLORS.dangerSoft};
    color: ${COLORS.danger};
    border-color: ${COLORS.dangerLine};
  }

  &:active {
    transform: scale(0.94);
  }
`;

/* ─── Component ─────────────────────────────────────────────────────────────── */

/**
 * CartItemRow — affiche un article du panier.
 *
 * @param {Object}  item        - CartItem : { itemType, itemId, sellerId, sellerName,
 *                                priceUSD, name, imageUrl, couponId, addedAt }
 * @param {boolean} unavailable - true si l'article n'est plus disponible (réponse /cart/validate)
 */
export function CartItemRow({ item, unavailable = false }) {
  const dispatch = useDispatch();

  const handleRemove = () => {
    dispatch(removeFromCart({ itemType: item.itemType, itemId: item.itemId }));
  };

  const price = parseFloat(item.priceUSD || '0');
  const formattedPrice = price.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <Row data-testid="cart-item-row" $unavailable={unavailable}>
      <Thumb
        src={item.imageUrl}
        alt={item.name || 'Item'}
        fallback="/assets/fallback-item.png"
        skeleton
      />

      <Info>
        <ItemName title={item.name}>{item.name || `Item #${item.itemId}`}</ItemName>
        {unavailable
          ? <UnavailableBadge>Unavailable</UnavailableBadge>
          : <TypeChip>{getItemTypeLabel(item.itemType)}</TypeChip>
        }
        {item.sellerName && (
          <ItemMeta>by {item.sellerName}</ItemMeta>
        )}
      </Info>

      <PriceText>${formattedPrice}</PriceText>

      <RemoveBtn
        type="button"
        data-testid="cart-item-remove"
        aria-label={`Remove ${item.name || 'item'} from cart`}
        onClick={handleRemove}
      >
        ×
      </RemoveBtn>
    </Row>
  );
}

export default CartItemRow;
